// =============================================================================
// Terminal Test Page - 测试终端功能
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { useAuth } from '../contexts/AuthContext';
import { workspaceService } from '../services/workspaceService';
import toast from 'react-hot-toast';
import 'xterm/css/xterm.css';

const TerminalTestPage = () => {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [currentTerminal, setCurrentTerminal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    loadWorkspaces();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (xtermRef.current) {
        xtermRef.current.dispose();
      }
    };
  }, []);

  const loadWorkspaces = async () => {
    try {
      const data = await workspaceService.getWorkspaces();
      setWorkspaces(data.filter(ws => ws.status === 'running'));
    } catch (error) {
      toast.error('Failed to load workspaces');
    }
  };

  const loadTerminals = async (workspaceId) => {
    try {
      const response = await fetch(`/api/terminals/workspace/${workspaceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTerminals(data.data);
      }
    } catch (error) {
      toast.error('Failed to load terminals');
    }
  };

  const createTerminal = async () => {
    if (!selectedWorkspace) {
      toast.error('Please select a workspace first');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/terminals', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workspaceId: selectedWorkspace,
          cols: 80,
          rows: 24,
          shell: '/bin/sh'
        })
      });

      const data = await response.json();
      if (data.success) {
        setCurrentTerminal(data.data);
        await loadTerminals(selectedWorkspace);
        initializeXterm(data.data.terminalId);
        toast.success('Terminal created successfully');
      } else {
        toast.error(data.error || 'Failed to create terminal');
      }
    } catch (error) {
      toast.error('Failed to create terminal');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeXterm = (terminalId) => {
    // 清理现有终端
    if (xtermRef.current) {
      xtermRef.current.dispose();
    }
    if (wsRef.current) {
      wsRef.current.close();
    }

    // 创建新的 xterm 实例
    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4'
      }
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);

    xtermRef.current = terminal;
    fitAddonRef.current = fitAddon;

    // 挂载到 DOM
    if (terminalRef.current) {
      terminal.open(terminalRef.current);
      fitAddon.fit();
    }

    // 显示连接信息
    terminal.writeln('🔌 Connecting to terminal...');
    terminal.writeln(`📋 Terminal ID: ${terminalId}`);
    terminal.writeln('⚠️  WebSocket connection not implemented yet');
    terminal.writeln('💡 Use REST API to interact with terminal');
    terminal.writeln('');

    // 模拟终端提示符
    terminal.write('$ ');

    // 处理用户输入（目前只是本地回显）
    terminal.onData((data) => {
      if (data === '\r') {
        terminal.write('\r\n$ ');
      } else if (data === '\u007F') { // Backspace
        terminal.write('\b \b');
      } else {
        terminal.write(data);
      }
    });

    // 窗口大小调整
    const handleResize = () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  const connectToTerminal = (terminal) => {
    setCurrentTerminal(terminal);
    initializeXterm(terminal.id);
  };

  const deleteTerminal = async (terminalId) => {
    try {
      const response = await fetch(`/api/terminals/${terminalId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Terminal deleted');
        if (currentTerminal?.id === terminalId) {
          setCurrentTerminal(null);
          if (xtermRef.current) {
            xtermRef.current.dispose();
            xtermRef.current = null;
          }
        }
        await loadTerminals(selectedWorkspace);
      }
    } catch (error) {
      toast.error('Failed to delete terminal');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Terminal Test</h1>
        <p className="text-muted-foreground">
          Test the terminal functionality with running workspaces
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 控制面板 */}
        <div className="lg:col-span-1 space-y-6">
          {/* 工作空间选择 */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Select Workspace</h3>
            </div>
            <div className="card-content">
              <select
                value={selectedWorkspace}
                onChange={(e) => {
                  setSelectedWorkspace(e.target.value);
                  if (e.target.value) {
                    loadTerminals(e.target.value);
                  }
                }}
                className="input w-full mb-4"
              >
                <option value="">Choose workspace...</option>
                {workspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name} ({ws.templateId})
                  </option>
                ))}
              </select>

              <button
                onClick={createTerminal}
                disabled={!selectedWorkspace || isLoading}
                className="btn btn-default w-full"
              >
                {isLoading ? 'Creating...' : 'Create Terminal'}
              </button>
            </div>
          </div>

          {/* 终端列表 */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Active Terminals</h3>
            </div>
            <div className="card-content">
              {terminals.length === 0 ? (
                <p className="text-sm text-muted-foreground">No terminals</p>
              ) : (
                <div className="space-y-2">
                  {terminals.map(terminal => (
                    <div
                      key={terminal.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        currentTerminal?.id === terminal.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => connectToTerminal(terminal)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">
                            {terminal.shell}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {terminal.cols}x{terminal.rows}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTerminal(terminal.id);
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 终端显示区域 */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Terminal</h3>
              {currentTerminal && (
                <div className="text-sm text-muted-foreground">
                  ID: {currentTerminal.terminalId}
                </div>
              )}
            </div>
            <div className="card-content">
              {currentTerminal ? (
                <div
                  ref={terminalRef}
                  className="w-full h-96 bg-black rounded"
                  style={{ minHeight: '400px' }}
                />
              ) : (
                <div className="flex items-center justify-center h-96 bg-secondary/20 rounded">
                  <p className="text-muted-foreground">
                    Select a workspace and create a terminal to get started
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminalTestPage;
